/** Match `.grid-section` span collapse in assets/utilities.css (max-width: 767.98px) */
function isProductGalleryMobileBreakpoint() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 767.98px)').matches;
}

function getGalleryShadowWrapper(swiperContainer) {
  const root = swiperContainer?.shadowRoot;
  if (!root) {
    return null;
  }
  return root.querySelector('[part="wrapper"]') || root.querySelector('.swiper-wrapper');
}

/** Survives Swiper mount/unmount — inline `style.order` is cleared by Swiper on breakpoint changes */
const QF_GALLERY_ORDER_DATASET = 'qfGalleryOrder';

function persistMainGallerySlideOrders(swiperContainer) {
  if (!swiperContainer || typeof swiperContainer.querySelectorAll !== 'function') {
    return;
  }
  swiperContainer.querySelectorAll('.main-swiper-slide').forEach((slide) => {
    let ord = typeof slide.style?.order === 'string' ? slide.style.order.trim() : '';
    if ((!ord || ord === 'auto') && slide.dataset[QF_GALLERY_ORDER_DATASET]) {
      return;
    }
    if (!ord || ord === 'auto') {
      if (typeof window === 'undefined') {
        return;
      }
      const computed = window.getComputedStyle(slide).order || '';
      if (computed && computed !== 'auto' && computed !== 'normal') {
        ord = computed;
      }
    }
    if (ord && ord !== 'auto') {
      slide.dataset[QF_GALLERY_ORDER_DATASET] = ord;
    }
  });
}

function restoreMainGallerySlideOrders(swiperContainer) {
  if (!swiperContainer || typeof swiperContainer.querySelectorAll !== 'function') {
    return;
  }
  swiperContainer.querySelectorAll('.main-swiper-slide').forEach((slide) => {
    const persisted = slide.dataset[QF_GALLERY_ORDER_DATASET];
    if (persisted !== undefined && persisted !== '') {
      slide.style.order = persisted;
    }
  });
}

const instances = new Map();

class ProductGalleryController {
  constructor(swiperContainer) {
    this.swiperContainer = swiperContainer;
    this.resizeTimer = null;
    this.handleResize = this.handleResize.bind(this);

    this.init();
  }

  init() {
    if (!this.swiperContainer) return;

    this.swiperContainer.dataset.productGalleryInitialized = 'true';
    this.update();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize, { passive: true });
    }
  }

  shouldEnable() {
    return isProductGalleryMobileBreakpoint();
  }

  update() {
    this.toggleSwiper(this.shouldEnable());
  }

  applyDesktopGrid(swiperContainer) {
    const applyGrid = () => {
      // Try shadowRoot access first
      if (swiperContainer.shadowRoot) {
        const wrapper = getGalleryShadowWrapper(swiperContainer);
        if (wrapper) {
          wrapper.style.display = 'grid';
          wrapper.style.gridTemplateColumns = 'repeat(12, 1fr)';

          // Use computed value for gap instead of CSS variables
          const computedStyle = getComputedStyle(document.documentElement);
          const gridGap = computedStyle.getPropertyValue('--grid-gap') || '1rem';
          wrapper.style.gap = gridGap;

          wrapper.style.transform = 'none';

          // Constrain the wrapper to its container
          wrapper.style.width = '100%';
          wrapper.style.maxWidth = '100%';
          wrapper.style.overflow = 'visible';

          // Style slotted content from light DOM
          const slotElement = wrapper.querySelector('slot');
          if (slotElement) {
            persistMainGallerySlideOrders(swiperContainer);

            // Get the assigned elements (actual content projected into the slot)
            const assignedElements = slotElement.assignedElements();

            // Style the slotted elements directly
            assignedElements.forEach((element) => {
              element.style.transform = 'none';
              element.style.width = 'auto';
              element.style.marginRight = '0';
              element.style.position = 'static';
              element.style.flex = 'none';
            });

            restoreMainGallerySlideOrders(swiperContainer);

            // Add CSS to the shadow root to style slotted content
            let styleSheet = wrapper.getRootNode().querySelector('style[data-grid-fix]');
            if (!styleSheet) {
              styleSheet = document.createElement('style');
              styleSheet.setAttribute('data-grid-fix', 'true');
              styleSheet.textContent = `
                ::slotted(*) {
                  transform: none !important;
                  width: auto !important;
                  margin-right: 0 !important;
                  position: static !important;
                  flex: none !important;
                }
              `;
              wrapper.getRootNode().appendChild(styleSheet);
            }
          }

          return true;
        }
      }

      // Fallback: Query the wrapper directly
      const wrapper = swiperContainer.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.display = 'grid';
        wrapper.style.gridTemplateColumns = 'repeat(12, 1fr)';
        wrapper.style.gap = 'clamp(var(--grid-gap-mobile), calc(var(--grid-intercept) + var(--grid-slope) * 1vw), var(--grid-gap))';
        wrapper.style.transform = 'none';
        persistMainGallerySlideOrders(swiperContainer);
        restoreMainGallerySlideOrders(swiperContainer);
        return true;
      }

      return false;
    };

    // Try immediately, with fallback for timing issues
    if (!applyGrid()) {
      // Use MutationObserver to watch for when the shadowRoot becomes available
      const observer = new MutationObserver(() => {
        if (applyGrid()) {
          observer.disconnect();
        }
      });

      // Watch for child additions and attribute changes
      observer.observe(swiperContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'init']
      });

      // Also try with timeouts as fallback
      setTimeout(() => {
        if (applyGrid()) {
          observer.disconnect();
        } else {
          setTimeout(() => {
            applyGrid();
            observer.disconnect();
          }, 500);
        }
      }, 100);
    }
  }

  cleanupDesktopGrid(swiperContainer) {
    const wrapper = getGalleryShadowWrapper(swiperContainer);

    if (wrapper) {
      // Reset wrapper styles
      wrapper.style.display = '';
      wrapper.style.gridTemplateColumns = '';
      wrapper.style.gap = '';
      wrapper.style.transform = '';
      wrapper.style.width = '';
      wrapper.style.maxWidth = '';
      wrapper.style.overflow = '';

      // Clean up slotted elements
      const slotElement = wrapper.querySelector('slot');
      if (slotElement) {
        const assignedElements = slotElement.assignedElements();

        assignedElements.forEach((element) => {
          element.style.transform = '';
          element.style.width = '';
          element.style.marginRight = '';
          element.style.position = '';
          element.style.flex = '';
        });
      }

      // Remove the CSS rules we added
      const styleSheet = wrapper.getRootNode().querySelector('style[data-grid-fix]');
      if (styleSheet) {
        styleSheet.remove();
      }
    }

    const lightWrapper = swiperContainer.querySelector(':scope > .swiper-wrapper');
    if (lightWrapper && lightWrapper !== wrapper) {
      lightWrapper.style.display = '';
      lightWrapper.style.gridTemplateColumns = '';
      lightWrapper.style.gap = '';
      lightWrapper.style.transform = '';
    }
  }

  wrapGalleryInitializeForDesktop(swiperContainer) {
    if (!swiperContainer || typeof swiperContainer.initialize !== 'function') {
      return;
    }
    const current = swiperContainer.initialize;
    if (current.__qfAdaptiveGalleryInit) {
      return;
    }
    if (!swiperContainer.__qfNativeGalleryInitialize) {
      swiperContainer.__qfNativeGalleryInitialize = current.bind(swiperContainer);
    }
    const native = swiperContainer.__qfNativeGalleryInitialize;
    const adaptive = function adaptiveGalleryInitialize(...args) {
      if (isProductGalleryMobileBreakpoint()) {
        return native ? native.apply(swiperContainer, args) : undefined;
      }
      return false;
    };
    adaptive.__qfAdaptiveGalleryInit = true;
    swiperContainer.initialize = adaptive;
  }

  toggleSwiper(shouldEnable) {
    const swiperContainer = this.swiperContainer;
    if (!swiperContainer) return;

    if (shouldEnable) {
      persistMainGallerySlideOrders(swiperContainer);
      // Clean up desktop grid styles
      this.cleanupDesktopGrid(swiperContainer);

      swiperContainer.setAttribute('init', 'true');
      swiperContainer.classList.remove('desktop');

      if (swiperContainer.__qfNativeGalleryInitialize) {
        swiperContainer.initialize = swiperContainer.__qfNativeGalleryInitialize;
      }

      if (swiperContainer.swiper && swiperContainer.swiper.destroyed) {
        if (typeof swiperContainer.initialize === 'function') {
          swiperContainer.initialize();
        }
      } else if (!swiperContainer.swiper && typeof swiperContainer.initialize === 'function') {
        swiperContainer.initialize();
      }
    } else {
      persistMainGallerySlideOrders(swiperContainer);
      const hadLiveSwiper = swiperContainer.swiper && !swiperContainer.swiper.destroyed;
      const wasDesktopGrid = swiperContainer.classList.contains('desktop');

      if (swiperContainer.swiper && !swiperContainer.swiper.destroyed) {
        swiperContainer.swiper.destroy(true, true);
      }

      swiperContainer.setAttribute('init', 'false');
      swiperContainer.classList.add('desktop');

      // Desktop-only resize: hero grid can be refreshed without re-running destroy + delayed init
      if (!hadLiveSwiper && wasDesktopGrid) {
        this.wrapGalleryInitializeForDesktop(swiperContainer);
        this.applyDesktopGrid(swiperContainer);
        return;
      }

      // Small delay after tearing down Swiper so shadow DOM / slides settle before grid styles
      setTimeout(() => {
        this.wrapGalleryInitializeForDesktop(swiperContainer);
        this.applyDesktopGrid(swiperContainer);
      }, 50);
    }
  }

  handleResize() {
    if (!this.swiperContainer?.isConnected) {
      this.destroy();
      return;
    }

    const clear = typeof window !== 'undefined' ? window.clearTimeout : clearTimeout;
    const schedule = typeof window !== 'undefined' ? window.setTimeout : setTimeout;

    if (this.resizeTimer) {
      clear(this.resizeTimer);
    }

    this.resizeTimer = schedule(() => {
      this.resizeTimer = null;
      this.update();
    }, 100);
  }

  destroy() {
    const clear = typeof window !== 'undefined' ? window.clearTimeout : clearTimeout;

    if (this.resizeTimer) {
      clear(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }

    if (this.swiperContainer?.swiper && !this.swiperContainer.swiper.destroyed) {
      this.swiperContainer.swiper.destroy(true, true);
    }

    if (this.swiperContainer) {
      this.swiperContainer.setAttribute('init', 'false');
      this.swiperContainer.classList.add('desktop');
      delete this.swiperContainer.dataset.productGalleryInitialized;
    }

    if (instances.has(this.swiperContainer)) {
      instances.delete(this.swiperContainer);
    }
  }
}

function findSwiperContainers(root = document) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  return Array.from(root.querySelectorAll('[main-gallery] swiper-container'));
}

function initGalleries(root = document) {
  const containers = findSwiperContainers(root);
  containers.forEach((container) => {
    if (!container || instances.has(container)) return;
    const instance = new ProductGalleryController(container);
    instances.set(container, instance);
  });
}

function teardownGalleries(root) {
  const containers = findSwiperContainers(root);
  containers.forEach((container) => {
    const instance = instances.get(container);
    if (!instance) return;
    instance.destroy();
  });
}

function onDomReady(callback) {
  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

onDomReady(() => initGalleries(document));

if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    initGalleries(event?.target || document);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    teardownGalleries(event?.target || document);
  });
}

if (typeof window !== 'undefined') {
  window.QF = window.QF || {};
  window.QF.productGallery = {
    init: initGalleries,
    destroy: teardownGalleries,
    instances,
    persistMainGallerySlideOrders,
    restoreMainGallerySlideOrders,
    QF_GALLERY_ORDER_DATASET,
  };
}
